/**
 * @file ui/src/pages/Terms.tsx
 * @description Public bilingual terms for Spark KNE services.
 * @copyright 2026 Seth Zhai. BSL 1.1
 */

import { useTranslation } from 'react-i18next'
import { setLocale as persistLocale } from '../i18n'
import styles from './Home.module.css'

type Locale = 'en' | 'zh'

const copy = {
  en: {
    navLabel: 'Terms navigation',
    switchLabel: '中文',
    title: 'Spark KNE Terms of Use',
    updated: 'Last updated: August 10, 2026',
    intro: 'These terms govern access to Spark KNE browser extensions, knowledge assessment services, and approved integrations operated by Seth Zhai during the public beta.',
    sections: [
      ['Service scope', [
        'Spark KNE assesses selected knowledge claims against sources available within the current KNE coverage and returns evidence-grounded trust states, source roles, evidence gaps, conflicts, and review needs.',
        'Spark returns knowledge trust assessments, not truth verdicts. source_supported means applicable sources were found in the current context; it does not guarantee that a claim is true. source_missing means KNE found no applicable source within its current coverage; it does not mean that a claim is false.',
        'Spark is not a substitute for professional medical, legal, financial, safety, or other qualified advice. High-risk results require independent review by an appropriate professional or authority.',
      ]],
      ['Beta access and accounts', [
        'Anonymous and signed-in usage may be limited by rolling quotas, public-beta capacity, platform rules, security controls, or service availability.',
        'You are responsible for keeping account credentials and access tokens secure and for activity performed through your account.',
        'Features, quotas, trust-state wording, supported platforms, and service availability may change during the beta. Material data-use changes will be reflected in the Privacy Policy before release.',
      ]],
      ['Acceptable use', [
        'Do not use Spark to violate law, harm others, bypass access controls, overload the service, probe private systems, distribute malware, or submit content you do not have the right to process.',
        'Do not systematically query, scrape, reconstruct, copy, or create a substitute for the KNE source registry, evidence graph, review records, trust-policy data, or other non-public KNE datasets.',
        'Automated or integrated access is allowed only through interfaces and credentials explicitly approved for that use and remains subject to rate limits and platform terms.',
      ]],
      ['User submissions', [
        'When you submit a source link, correction, challenge, claim, note, or feedback, you confirm that you may provide that material and that it does not contain credentials or unlawful content.',
        'You grant Spark KNE a non-exclusive, worldwide, royalty-free license to store, review, reproduce, transform, and use the submitted metadata and content to operate and improve Spark and KNE. Third-party source ownership is not transferred to Spark.',
        'Submissions enter a review queue and do not automatically become certified KNE knowledge. Spark may reject, remove, anonymize, or retain a review record when needed for quality, safety, abuse prevention, or legal obligations.',
      ]],
      ['Intellectual property', [
        'Spark KNE branding, private KNE datasets, evidence graph, source registry, trust-policy implementation, review records, and service content remain owned by their respective rights holders.',
        'Any separately published open-source client code is governed by the license included with that code. Open-source availability does not grant rights to private KNE data, service credentials, or Spark KNE trademarks.',
      ]],
      ['Availability and termination', [
        'The beta service is provided on an as-available basis without a guarantee of uninterrupted access, complete source coverage, error-free operation, or suitability for a particular decision.',
        'Spark may suspend access that creates security, legal, abuse, capacity, or platform-compliance risk. You may stop using the service at any time and request account or server-record deletion as described in the Privacy Policy.',
      ]],
      ['Privacy and contact', [
        'The Spark KNE Privacy Policy explains collection, processing, storage, retention, sharing, security, and deletion of user data.',
        'Questions about these terms or the service can be sent to seth@knespark.com. These beta terms should not be treated as legal advice and will be reviewed before commercial launch.',
      ]],
    ],
  },
  zh: {
    navLabel: '使用条款导航',
    switchLabel: 'EN',
    title: 'Spark KNE 使用条款',
    updated: '最后更新：2026 年 8 月 10 日',
    intro: '本条款适用于 Seth Zhai 在公开测试期间运营的 Spark KNE 浏览器插件、知识可信评估服务及经批准的平台集成。',
    sections: [
      ['服务范围', [
        'Spark KNE 会将用户选择的知识主张与 KNE 当前覆盖范围内的来源进行比较，并返回基于证据的可信状态、来源角色、证据缺口、冲突和复核要求。',
        'Spark 返回知识可信评估，不提供真理裁决。source_supported 表示当前语境下找到了适用来源，不保证主张必然真实；source_missing 表示 KNE 当前覆盖内没有找到适用来源，不代表主张错误。',
        'Spark 不能替代医学、法律、金融、安全或其它专业意见。高风险结果必须由适当的专业人员或权威机构独立复核。',
      ]],
      ['公测访问与账号', [
        '匿名和登录使用可能受到滚动额度、公测容量、平台规则、安全控制或服务可用性的限制。',
        '你有责任保护账号凭证和访问 token，并对通过账号进行的活动负责。',
        '公测期间，功能、额度、可信状态文案、支持平台和服务可用性可能调整。涉及数据使用的实质变化会在发布前同步更新隐私政策。',
      ]],
      ['可接受使用', [
        '不得使用 Spark 违法、伤害他人、绕过访问控制、压垮服务、探测私有系统、传播恶意软件，或提交你无权处理的内容。',
        '不得通过系统性查询、抓取、重建或复制等方式，创建 KNE 来源库、证据图谱、复核记录、可信策略数据或其它非公开 KNE 数据的替代品。',
        '自动化或集成调用只能通过明确批准的接口和凭证进行，并受额度及相关平台条款约束。',
      ]],
      ['用户提交', [
        '提交来源链接、纠正、质疑、知识主张、备注或反馈时，你确认有权提供相应内容，且其中不包含凭证或违法内容。',
        '你授予 Spark KNE 一项非独占、全球、免版税的许可，用于存储、审核、复制、转换和使用你提交的元数据与内容，以运行和改进 Spark 与 KNE。第三方来源的所有权不会因此转移给 Spark。',
        '提交内容进入复核队列，不会自动成为 KNE 已认证知识。出于质量、安全、防滥用或法律义务，Spark 可以拒绝、删除、匿名化提交内容，或保留必要的复核记录。',
      ]],
      ['知识产权', [
        'Spark KNE 品牌、私有 KNE 数据、证据图谱、来源库、可信策略实现、复核记录和服务内容仍归各自权利人所有。',
        '单独发布的开源客户端代码适用其代码仓库内的许可证。开源代码不授予对私有 KNE 数据、服务凭证或 Spark KNE 商标的权利。',
      ]],
      ['可用性与终止', [
        '公测服务按现状和可用状态提供，不保证持续可访问、来源覆盖完整、运行无错误或适合某项具体决策。',
        '如访问行为造成安全、法律、滥用、容量或平台合规风险，Spark 可以暂停相应访问。你可以随时停止使用，并按照隐私政策申请删除账号或服务端记录。',
      ]],
      ['隐私与联系', [
        'Spark KNE 隐私政策说明用户数据的收集、处理、存储、保留、共享、安全和删除方式。',
        '条款或服务问题请联系 seth@knespark.com。本公测条款不构成法律意见，商业化上线前仍需进行正式法律审阅。',
      ]],
    ],
  },
} as const

export default function TermsPage() {
  const { i18n } = useTranslation()
  const locale: Locale = i18n.language === 'en' ? 'en' : 'zh'
  const t = copy[locale]

  return (
    <main className={styles.root}>
      <section className={styles.policyPage}>
        <nav className={styles.nav} aria-label={t.navLabel}>
          <a className={styles.brand} href="/">
            <span className={styles.brandMark} />
            <span>Spark KNE</span>
          </a>
          <button
            className={styles.langToggle}
            type="button"
            onClick={() => persistLocale(locale === 'en' ? 'zh' : 'en')}
          >
            {t.switchLabel}
          </button>
        </nav>
        <header className={styles.policyHeader}>
          <p className={styles.kicker}>Terms</p>
          <h1>{t.title}</h1>
          <span>{t.updated}</span>
          <p>{t.intro}</p>
        </header>
        <div className={styles.policySections}>
          {t.sections.map(([title, items]) => (
            <section key={title} className={styles.policyBlock}>
              <h2>{title}</h2>
              <ul>
                {items.map(item => <li key={item}>{item}</li>)}
              </ul>
            </section>
          ))}
        </div>
      </section>
    </main>
  )
}
